import axios from 'axios';

// Base URLs
export const API_ORIGIN =
  process.env.REACT_APP_API_ORIGIN || 'https://api.stayeasy.io';

export const API_BASE =
  process.env.REACT_APP_API_BASE_URL || `${API_ORIGIN}/api/v1`;

const sharedConfig = {
  withCredentials: true,
  withXSRFToken: true,
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
  headers: {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
};

// Shared API client for all versioned host/admin requests.
const client = axios.create({
  baseURL: API_BASE,
  ...sharedConfig,
});

// Origin-level client is required because Sanctum's CSRF endpoint is outside /api/v1.
const csrfClient = axios.create({
  baseURL: API_ORIGIN,
  ...sharedConfig,
});

let csrfReady = false;

// Always use this immediately before login so an expired or replaced cookie is refreshed.
export async function initialiseCsrf() {
  await csrfClient.get('/sanctum/csrf-cookie');
  csrfReady = true;
}

// Cached helper for other state-changing requests during an active session.
export async function ensureCsrf() {
  if (csrfReady) return;
  await initialiseCsrf();
}

// Response interceptor: unwrap the standardized envelope { success, message, data, meta? }
client.interceptors.response.use(
  (res) => {
    if (res.data && typeof res.data === 'object' && 'success' in res.data) {
      const unwrapped = res.data.data;
      if (res.data.meta && unwrapped && typeof unwrapped === 'object') {
        try {
          Object.defineProperty(unwrapped, '__meta', {
            value: res.data.meta,
            enumerable: false,
          });
        } catch (_) {}
      }
      res.data = unwrapped;
    }
    return res;
  },
  (err) => {
    const payload = err.response?.data;
    const normalized = {
      status: err.response?.status,
      message: payload?.message || err.message || 'Request failed',
      errors: payload?.errors || null,
      raw: err,
    };

    if ([401, 419].includes(normalized.status)) csrfReady = false;
    return Promise.reject(normalized);
  }
);

export function resetCsrf() {
  csrfReady = false;
}

export default client;
